import { Skeleton } from 'primeng/skeleton';
import { TranslatePipe } from '@ngx-translate/core';

import { NgTemplateOutlet } from '@angular/common';
import { input, output, computed, Component, TemplateRef, contentChild, ChangeDetectionStrategy } from '@angular/core';

import { PhoneFormatPipe } from '@shared/pipes';

import { LoadingOverlay } from '@shared/ui/loading-overlay';
import { TableStateCard, type TableBusyState, type TableContentState } from '@shared/ui/table-state';
import {
  TABLE_IMPORTS,
  ACTIONS_COLUMN,
  TABLE_SORT_IMPORTS,
  type TableSortEvent,
  type TableSortDirection,
} from '@shared/ui/table';

import { type EntityAction } from '@shared/utils/entity-actions';

import { type Firm, type FirmOrderBy } from '../../models';

export interface FirmTableSortChangeEvent {
  orderBy: FirmOrderBy;
  orderDir: TableSortDirection;
}

const DATA_COLUMNS = ['name', 'address', 'contactName', 'phone', 'email'] as const;

const NO_DATA_TITLE_KEYS: Record<Exclude<TableContentState, 'rows'>, string> = {
  empty: 'entities.firm.table.empty',
  noResults: 'common.table.noResults',
  error: 'entities.firm.table.loadError',
};

@Component({
  selector: 'app-firms-table',
  imports: [
    Skeleton,
    TABLE_IMPORTS,
    TranslatePipe,
    LoadingOverlay,
    TableStateCard,
    PhoneFormatPipe,
    NgTemplateOutlet,
    TABLE_SORT_IMPORTS,
  ],
  templateUrl: './firms-table.html',
  styleUrl: './firms-table.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FirmsTable {
  public readonly items = input.required<Firm[]>();
  public readonly busyState = input<TableBusyState>('idle');
  public readonly contentState = input<TableContentState>('rows');
  public readonly orderBy = input<FirmOrderBy | undefined>();
  public readonly orderDir = input<TableSortDirection | undefined>();
  public readonly skeletonRowsCount = input(15);
  public readonly rowActions = input<EntityAction<Firm>[]>([]);

  protected readonly emptyTpl = contentChild('empty', { read: TemplateRef });
  protected readonly noResultsTpl = contentChild('noResults', { read: TemplateRef });
  protected readonly errorTpl = contentChild('error', { read: TemplateRef });

  protected readonly isInitialLoad = computed(() => this.busyState() === 'initialLoading');
  protected readonly rows = computed<Firm[] | unknown[]>(() =>
    this.isInitialLoad() ? Array.from({ length: this.skeletonRowsCount() }) : this.items(),
  );

  protected readonly showLoadingOverlay = computed(() => this.busyState() === 'reloading');
  protected readonly disableSorting = computed(() => this.busyState() !== 'idle');

  protected readonly defaultNoDataTitleKey = computed(
    () => NO_DATA_TITLE_KEYS[this.contentState() as Exclude<TableContentState, 'rows'>],
  );

  protected readonly displayedColumns = computed(() =>
    this.rowActions().length > 0 ? [...DATA_COLUMNS, ACTIONS_COLUMN] : DATA_COLUMNS,
  );

  public readonly sortChange = output<FirmTableSortChangeEvent>();

  protected onSortChange(event: TableSortEvent): void {
    this.sortChange.emit({
      orderBy: event.active as FirmOrderBy,
      orderDir: event.direction,
    });
  }
}
